import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useFocusTrap } from "./useFocusTrap";

// Every padding here is a DS scale step (--space-*). It used to run
// px-8 pt-8 pb-6 on the body, pl-8 pr-6 py-4 on the
// footer and right-[18px] top-[21px] on the close: seven values, six of them
// off-scale, carried over literally from Figma. They are now 32 / 32 / 24 and
// 32 / 16 and 16 / 16. The footer is symmetric where it was 30 left and 22
// right, which moves the primary action 10px in.
//
// The card and its scrim ENTER together (.dova-modal-card / .dova-modal-scrim),
// so the layer reads as one surface arriving rather than a box appearing on an
// already-dimmed page. Centre origin, which is right here and nowhere else: a
// modal is not anchored to a trigger. There is no EXIT animation — React
// unmounts the tree on close, and holding it for an outro would mean owning a
// closing state this component does not have.
//
// Modal — DS §4.15. Scrim rgba(20,20,15,.4) + blur(4px); 460 default / 520
// wide; footer = link left / primary right. Esc + scrim-click close; focus
// trap (shared useFocusTrap); body scroll lock; role="dialog" + aria-modal.

export interface ModalProps {
    open: boolean;
    onClose: () => void;
    eyebrow?: string;
    title?: string;
    ariaLabel?: string;
    /** Legacy width flag (520px). Prefer `size`; kept as an alias. */
    /** `danger` tints the footer — ConfirmModal passes it for destructive confirms. */
    footerTone?: "default" | "danger";
    wide?: boolean;
    /** Width preset: default 460 / wide 520 / xl 760 / 2xl 920. Overrides `wide` when set. */
    size?: "default" | "wide" | "lg" | "xl" | "2xl";
    /** 0–100. When set, a progress strip fills the card's top edge. */
    progress?: number;
    /** Heading scale: "default" → h2 (22px); "lg" → h1 (30px) with more padding. */
    titleSize?: "default" | "lg";
    /**
     * The body FILLS the modal instead of scrolling, and its child owns any scrolling.
     *
     * For a child that pins its own footer — `RecurringWindowGrid` with `fillHeight` — where a
     * scrolling body would push that footer below the fold. The two props go together: passing
     * one without the other gives either two scrollbars or none.
     */
    bodyFill?: boolean;
    footer?: ReactNode;
    /** When false, clicking the scrim does NOT close the modal — useful for
     * flows where unsaved work shouldn't be lost on an accidental outside
     * click. Esc + the X button still close. Default true. */
    dismissOnScrim?: boolean;
    children?: ReactNode;
}

/** Matches --dur-base, the exit animation's duration. A number because the
 *  unmount is a setTimeout: animationend on an element about to be removed is
 *  the more fragile of the two. */
const MODAL_EXIT_MS = 200;

export function Modal({
    open,
    onClose,
    eyebrow,
    title,
    ariaLabel,
    wide,
    size,
    progress,
    titleSize = "default",
    footerTone = "default",
    bodyFill = false,
    footer,
    dismissOnScrim = true,
    children,
}: ModalProps) {
    const dialogRef = useRef<HTMLDivElement>(null);

    // The modal OUTLIVES `open` by one animation so it can leave.
    //
    // `rendered` is what actually gates the markup; `open` only drives which
    // direction it plays. Without this the tree unmounts on the same tick the
    // prop flips and there is nothing left to animate — which is why this
    // component had an entrance and no exit for so long.
    //
    // Reduced motion skips the hold entirely: no animation runs, so waiting
    // 200ms would just be 200ms of a modal that should already be gone.
    const [rendered, setRendered] = useState(open);
    const closing = rendered && !open;

    useEffect(() => {
        if (open) {
            setRendered(true);
            return;
        }
        if (!rendered) return;
        // Guarded, not assumed: jsdom does not implement matchMedia, and an
        // unguarded call here throws inside a passive effect — which took down
        // 34 tests across seven files that have nothing to do with motion.
        // Absent the API, skip the hold: no animation can run anyway.
        const reduce =
            typeof window.matchMedia === "function" &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reduce) {
            setRendered(false);
            return;
        }
        const t = window.setTimeout(() => setRendered(false), MODAL_EXIT_MS);
        return () => window.clearTimeout(t);
    }, [open, rendered]);

    // Trap and lock follow `open`, NOT `rendered`: a closing modal is inert. It
    // must not hold focus or the body scroll while it fades, or a dismissal
    // leaves the page locked for the length of the animation.
    useFocusTrap(dialogRef, open);

    useEffect(() => {
        if (!open) return;
        const previouslyFocused = document.activeElement as HTMLElement | null;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        dialogRef.current?.focus();

        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.preventDefault();
                onClose();
            }
        };
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
            previouslyFocused?.focus?.();
        };
    }, [open, onClose]);

    if (!rendered) return null;

    const maxW =
        size === "2xl"
            ? "max-w-[920px]"
            : size === "xl"
              ? "max-w-[760px]"
              : // 600px — the design's card width for every NON-calendar frame (2486:112/270/
                // 1176/1075/999). The two calendar frames are 920 (`2xl`). Added additively; no
                // existing consumer moves.
                size === "lg"
                ? "max-w-[600px]"
              : size === "wide" || (size === undefined && wide)
                ? "max-w-[520px]"
                : "max-w-[460px]";

    return createPortal(
        <div
            aria-hidden={closing || undefined}
            className={`${closing ? "dova-modal-scrim-out pointer-events-none" : "dova-modal-scrim"} fixed inset-0 z-50 flex items-center justify-center bg-[rgba(20,20,15,0.4)] p-8 backdrop-blur-[4px]`}
            onMouseDown={(e) => {
                if (!dismissOnScrim) return;
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-label={ariaLabel ?? title ?? "Dialog"}
                tabIndex={-1}
                className={`${closing ? "dova-modal-card-out" : "dova-modal-card"} relative flex max-h-[calc(100vh-72px)] w-full flex-col overflow-hidden rounded-[var(--radius-lg)] bg-surface shadow-[0_24px_60px_rgba(0,0,0,0.14),0_4px_16px_rgba(0,0,0,0.06)] outline-none ${maxW}`}
            >
                {/* 6px and coral, matching the wizard's line. It was 2.87px (a
                    Figma measurement kept literally) on --interactive: a
                    hairline that read as an artefact of the card's top edge, in
                    the token that now means focus and selection. Progress is
                    neither. */}
                {typeof progress === "number" && (
                    <div className="h-1.5 w-full shrink-0 bg-border">
                        <div
                            className="h-full w-full origin-left bg-cta"
                            style={{
                                transform: `scaleX(${progress / 100})`,
                                // Inline for the same reason as ProgressBar — see its note.
                                transition: "transform var(--dur-base) var(--ease-out)",
                            }}
                        />
                    </div>
                )}
                {/* Close stays pinned to the card so it survives body scroll. */}
                <button
                    type="button"
                    aria-label="Close"
                    onClick={onClose}
                    className="absolute right-4 top-4 z-10 rounded-[var(--radius-sm)] px-2 py-1 text-xl leading-none text-tertiary hover:bg-black/[0.04] hover:text-primary"
                >
                    ×
                </button>
                {/* Body scrolls when content is taller than the viewport; footer stays pinned. */}
                <div
                    className={`min-h-0 flex-1 px-8 pt-8 ${
                        bodyFill ? "flex flex-col overflow-hidden pb-0" : "overflow-y-auto pb-6"
                    }`}
                >
                    {/* The gap to the body lives on this WRAPPER, not on the
                        heading. portal-www's main.css sets `margin: 0` on bare
                        `h1`/`h2` outside any cascade layer, which beats
                        `@layer utilities` — so the `mb-8` that used to sit on
                        the h1 had never applied, and the title butted straight
                        into the content (QA-35, same trap as QA-31).

                        Only the `lg` branch is spaced. The default `h2` branch
                        carries an equally dead `mb-2` and nine modals have
                        been laid out around its absence; waking that one up is
                        its own pass, not a side effect of this one. */}
                    {(eyebrow || title) && (
                        <div className={titleSize === "lg" ? "mb-6" : undefined}>
                            {eyebrow && (
                                <div className={`dova-eyebrow ${titleSize === "lg" ? "mb-2" : "mb-2"}`}>
                                    {eyebrow}
                                </div>
                            )}
                            {title &&
                                (titleSize === "lg" ? (
                                    <h1 className="font-serif text-2xl font-medium leading-[1.15] tracking-[-0.01em] text-primary">
                                        {title}
                                    </h1>
                                ) : (
                                    <h2 className="mb-2 font-serif text-xl font-medium leading-[1.3] tracking-[-0.005em] text-primary">
                                        {title}
                                    </h2>
                                ))}
                        </div>
                    )}
                    {children}
                </div>
                {footer && (
                    <div
                        className={`flex shrink-0 items-center justify-between gap-4 border-t px-8 py-4 ${footerTone === "danger" ? "border-danger-bd bg-danger-bg" : "border-border bg-canvas"}`}
                    >
                        {footer}
                    </div>
                )}
            </div>
        </div>,
        document.body,
    );
}
